import React, { useMemo } from "react";
import { View, StyleSheet } from "react-native";
import { MessageComposer } from "../../components/chatNewInterface/chatLayout/messageComposer/MessageComposer";

interface AttachedItem {
  id: string;
  name: string;
  duedate?: string | number | Date;
}

interface AttachedFile {
  id: string;
  fileName: string;
  fileURL?: string;
  isLoading?: boolean;
}

const todoSuggestions = [
  { text: "Give me a game plan for this" },
  { text: "Break this into subtasks" },
  { text: "Build me a timeline to finish this" },
  { text: "What resources or materials do I need?" },
];

const fileSuggestions = [
  { text: "Summarize this for me" },
  { text: "Quiz me on this material" },
  { text: "What would likely be on a test about this?" },
  { text: "What are the key takeaways?" },
];

const assignmentSuggestions = [
  { text: "What should I do first?" },
  { text: "Break this into smaller steps" },
  { text: "What topics should I review first?" },
  { text: "How will this be graded?" },
];

interface ChatInputProps {
  value: string;
  onChangeText: (text: string) => void;
  onSubmit: () => void;

  disabled?: boolean;
  placeholder?: string;
  showSuggestions?: boolean;
  activeWorkspaceType: "general" | "course" | "support";

  // attachments (already computed)
  attachedTasks: AttachedItem[];
  attachedAssignments: AttachedItem[];
  attachedFiles: AttachedFile[];

  // actions
  onAddTask: () => void;
  onAddAssignment: () => void;
  onAddFile: () => void;
  onRemoveAttachment: () => void;

  // modals
  taskModal: React.ReactNode;
  assignmentModal: React.ReactNode;
}

const ChatInput = ({
  value,
  onChangeText,
  onSubmit,
  disabled,
  placeholder,
  showSuggestions = true,
  activeWorkspaceType,

  attachedTasks,
  attachedAssignments,
  attachedFiles,
  onAddTask,
  onAddAssignment,
  onAddFile,
  onRemoveAttachment,

  taskModal,
  assignmentModal,
}: ChatInputProps) => {
  const suggestedPrompts = useMemo(() => {
    if (!showSuggestions) return [];

    if (attachedTasks.length > 0) return todoSuggestions;
    if (attachedFiles.length > 0) return fileSuggestions;
    if (attachedAssignments.length > 0) return assignmentSuggestions;

    return []; // No suggestions when nothing is attached
  }, [showSuggestions, attachedTasks, attachedFiles, attachedAssignments]);

  return (
    <View style={styles.container}>
      <MessageComposer
        disabled={disabled}
        message={value}
        setMessage={onChangeText}
        onSendMessage={onSubmit}
        placeholder={placeholder}
        suggestedPrompts={suggestedPrompts}
        addTask={onAddTask}
        addAssignment={onAddAssignment}
        addFile={onAddFile}
        removeTask={onRemoveAttachment}
        removeAssignment={onRemoveAttachment}
        removeFile={onRemoveAttachment}
        attachedTasks={attachedTasks}
        attachedAssignments={attachedAssignments}
        attachedFiles={attachedFiles}
      />

      {taskModal}
      {assignmentModal}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: "100%",
    paddingVertical: 0,
    paddingHorizontal: 0,
  },
});

export default ChatInput;
